import { axiosClient } from "./axios-client";
import { api } from "./endpoints";

// Raw shape returned by the admin API (backend/src/models/Project.js).
type ProjectRecord = {
  _id: string;
  title: string;
  slug: string;
  thumbnail: string;
  technologies: string[];
  description: string;
  content: string;
  industry: string;
  completedDate: string;
  status: "draft" | "published";
  featured: boolean;
};

type ListEnvelope<T> = {
  success: boolean;
  data: T[];
  meta: { total: number; page: number; limit: number };
};

type ItemEnvelope<T> = {
  success: boolean;
  data: T;
};

// A per-project accent color is a display concept the backend doesn't (and shouldn't) model —
// standardized to the site's brand primary purple (see CLAUDE.md Color System).
const ACCENT = "#783DD7";

// The site's UI (portfolio-journey.tsx) is built around this shape.
export type Project = {
  slug: string;
  name: string;
  category: string;
  description: string;
  tech: string[];
  image: string;
  accent: string;
  completedDate: string;
  /** Not modeled in the backend schema — only ever set on the static fallback content. */
  liveUrl?: string;
};

// The full case-study detail page additionally needs the long-form rich-text body (admin-authored
// HTML, see HtmlContent) — kept as a separate type (rather than adding `content` to `Project`)
// since the list views never fetch it.
export type ProjectDetail = Project & { content: string };

function toProjectViewModel(record: ProjectRecord, index: number): Project {
  return {
    slug: record.slug,
    name: record.title,
    category: record.industry || "Client Project",
    description: record.description,
    tech: record.technologies,
    image: record.thumbnail,
    accent: ACCENT,
    completedDate: record.completedDate,
  };
}

function toProjectDetailViewModel(record: ProjectRecord, index: number): ProjectDetail {
  return { ...toProjectViewModel(record, index), content: record.content };
}

async function fetchProjectRecords(): Promise<ProjectRecord[]> {
  // The response interceptor already unwraps axios's own `response.data`, so what resolves here
  // is the backend's own envelope: { success, data, meta } — not a bare array.
  try {
    const envelope = (await axiosClient.get(api.getProjects)) as unknown as ListEnvelope<ProjectRecord>;
    return envelope.data;
  } catch {
    return [];
  }
}

export const getProjects = async (): Promise<Project[]> => {
  const records = await fetchProjectRecords();
  return records.filter((record) => record.status === "published").map(toProjectViewModel);
};

// Used for the homepage's "Selected work" section — admin-curated via the Featured toggle on
// the dashboard's Projects screen, rather than always showing the newest/first N projects.
export const getFeaturedProjects = async (): Promise<Project[]> => {
  const records = await fetchProjectRecords();
  return records
    .filter((record) => record.status === "published" && record.featured)
    .map(toProjectViewModel);
};

// Used by the case study detail page (/portfolio/[slug]) to render the admin-authored long-form
// HTML body alongside the same fields the list view uses. Index is looked up against the full
// published list so the accent color stays consistent with the card the user clicked from.
export const getProjectBySlug = async (slug: string): Promise<ProjectDetail | null> => {
  try {
    const [envelope, records] = await Promise.all([
      axiosClient.get(`${api.getProjects}/slug/${slug}`) as unknown as Promise<ItemEnvelope<ProjectRecord>>,
      fetchProjectRecords(),
    ]);
    const record = envelope.data;
    if (record.status !== "published") return null;
    const index = records.filter((r) => r.status === "published").findIndex((r) => r.slug === slug);
    return toProjectDetailViewModel(record, index === -1 ? 0 : index);
  } catch {
    return null;
  }
};
